import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import MovieCard from '@/components/feature/MovieCard';
import { movies } from '@/mocks/movies';

const catNameMap: Record<string, string> = {
  dianying: '电影',
  dianshiju: '电视剧',
  zongyi: '综艺',
  dongman: '动漫',
};

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const movie = movies.find((m) => m.id === Number(id));

  if (!movie) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 pb-10">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6 flex flex-col items-center justify-center py-20">
            <i className="ri-error-warning-line text-5xl text-foreground-400 mb-4"></i>
            <h2 className="text-xl font-bold text-foreground-800 mb-2">影片未找到</h2>
            <Link to="/" className="text-primary-500 hover:text-primary-600 transition-colors cursor-pointer">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const relatedMovies = movies
    .filter((m) => m.category === movie.category && m.id !== movie.id)
    .slice(0, 6);

  const catLabel = catNameMap[movie.category] || movie.category;

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Movie',
    name: movie.title,
    description: movie.description,
    image: movie.poster,
    dateCreated: `${movie.year}`,
    director: movie.director ? {
      '@type': 'Person',
      name: movie.director,
    } : undefined,
    actor: movie.actors.map((a) => ({
      '@type': 'Person',
      name: a,
    })),
    countryOfOrigin: movie.region,
    duration: movie.duration,
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 pb-10">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: catLabel, href: `/category/${movie.category}` },
                { label: movie.title },
              ]}
            />

            <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
              <div className="md:col-span-1">
                <div className="sticky top-24">
                  <div className="rounded-lg overflow-hidden bg-background-200 aspect-[2/3]">
                    <img
                      src={movie.poster}
                      alt={movie.title}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  <div className="mt-4 flex flex-col gap-2">
                    <Link
                      to={`/article/${movie.id}`}
                      className="whitespace-nowrap w-full flex items-center justify-center gap-2 py-3 bg-primary-500 text-white text-sm font-semibold rounded-lg hover:bg-primary-600 transition-colors cursor-pointer"
                    >
                      <i className="ri-play-fill text-lg"></i>
                      立即播放
                    </Link>
                    <button className="whitespace-nowrap w-full flex items-center justify-center gap-2 py-2.5 border border-background-300 text-foreground-700 text-sm font-medium rounded-lg hover:bg-background-100 transition-colors cursor-pointer">
                      <i className="ri-heart-line"></i>
                      收藏影片
                    </button>
                  </div>
                </div>
              </div>

              <div className="md:col-span-2">
                <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
                  {movie.title}
                </h1>
                {movie.englishTitle && (
                  <p className="text-sm text-foreground-500 mt-1">{movie.englishTitle}</p>
                )}

                <div className="flex flex-wrap items-center gap-2 mt-4">
                  {movie.rating > 0 && (
                    <span className="px-3 py-1 bg-accent-500/15 text-accent-600 text-sm font-bold rounded-full">
                      ⭐ {movie.rating}
                    </span>
                  )}
                  {movie.isVip && (
                    <span className="px-3 py-1 bg-accent-500 text-background-950 text-xs font-bold rounded-full">
                      VIP
                    </span>
                  )}
                  {movie.isNew && (
                    <span className="px-3 py-1 bg-primary-500/15 text-primary-600 text-xs font-bold rounded-full">
                      最新
                    </span>
                  )}
                  {movie.isHot && (
                    <span className="px-3 py-1 bg-secondary-500/15 text-secondary-600 text-xs font-bold rounded-full">
                      热门
                    </span>
                  )}
                </div>

                <div className="mt-6 p-5 bg-background-100 rounded-lg border border-background-200/50">
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                    <div>
                      <span className="text-foreground-500">年份：</span>
                      <span className="text-foreground-800 font-medium">{movie.year}</span>
                    </div>
                    <div>
                      <span className="text-foreground-500">地区：</span>
                      <span className="text-foreground-800 font-medium">{movie.region}</span>
                    </div>
                    <div>
                      <span className="text-foreground-500">时长：</span>
                      <span className="text-foreground-800 font-medium">{movie.duration}</span>
                    </div>
                    {movie.director && (
                      <div>
                        <span className="text-foreground-500">导演：</span>
                        <span className="text-foreground-800 font-medium">{movie.director}</span>
                      </div>
                    )}
                    <div className="col-span-2">
                      <span className="text-foreground-500">状态：</span>
                      <span className="text-foreground-800 font-medium">{movie.episodeInfo}</span>
                    </div>
                    {movie.actors.length > 0 && (
                      <div className="col-span-full">
                        <span className="text-foreground-500">主演：</span>
                        <span className="text-foreground-800 font-medium">{movie.actors.join(' / ')}</span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="mt-6">
                  <h3 className="font-heading text-lg font-bold text-foreground-900 mb-3">影片简介</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">{movie.description}</p>
                </div>

                <div className="mt-4 flex flex-wrap gap-2">
                  {movie.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1 bg-background-100 text-foreground-600 text-xs rounded-full border border-background-200/70"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>

                {movie.stills.length > 0 && (
                  <div className="mt-8">
                    <h3 className="font-heading text-lg font-bold text-foreground-900 mb-4">精彩剧照</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {movie.stills.map((still, i) => (
                        <div key={i} className="rounded-lg overflow-hidden bg-background-200 aspect-video">
                          <img
                            src={still}
                            alt={`${movie.title} 剧照 ${i + 1}`}
                            className="w-full h-full object-cover"
                            loading="lazy"
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {relatedMovies.length > 0 && (
              <div className="mt-12">
                <div className="flex items-center justify-between mb-5">
                  <h3 className="font-heading text-lg font-bold text-foreground-900">相关推荐</h3>
                  <Link
                    to={`/category/${movie.category}`}
                    className="whitespace-nowrap text-sm text-primary-500 hover:text-primary-600 transition-colors cursor-pointer font-medium"
                  >
                    更多 <i className="ri-arrow-right-line"></i>
                  </Link>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 md:gap-5">
                  {relatedMovies.map((m) => (
                    <MovieCard key={m.id} movie={m} size="normal" />
                  ))}
                </div>
              </div>
            )}
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}